import {
  SimpleFilter,
  SoundTouch,
  WebAudioBufferSource,
} from '@soundtouchjs/core';

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const PROCESSING_TAIL_PADDING_SECONDS = 1;
const TRIM_SILENCE_THRESHOLD = 0.0008;
const TRIM_SILENCE_TAIL_FRAMES = 2048;

export const mapSpeechRateToTempo = (speechRate) => {
  const value = clamp(Number(speechRate) || 0, -100, 100);
  if (value <= 0) {
    return 1 + (value / 100) * 0.5;
  }
  return 1 + (value / 100) * 1;
};

export const mapPitchToSemitones = (pitch) => {
  const value = clamp(Number(pitch) || 0, -100, 100);
  return (value / 100) * 12;
};

const decodeAudioArrayBuffer = async ({ audioUrl, audioBlob }) => {
  if (audioBlob) {
    return audioBlob.arrayBuffer();
  }

  if (!audioUrl) {
    throw new Error('未提供可处理的音频源');
  }

  const response = await fetch(audioUrl);
  if (!response.ok) {
    throw new Error(`音频下载失败: ${response.status} ${response.statusText}`);
  }

  return response.arrayBuffer();
};

export const decodeAudioBufferFromSource = async ({ audioUrl, audioBlob }) => {
  const arrayBuffer = await decodeAudioArrayBuffer({ audioUrl, audioBlob });
  const AudioContext = window.AudioContext || window.webkitAudioContext;
  const decodeContext = new AudioContext();

  try {
    return await decodeContext.decodeAudioData(arrayBuffer.slice(0));
  } finally {
    if (decodeContext.state !== 'closed') {
      await decodeContext.close();
    }
  }
};

const appendSilenceToAudioBuffer = (audioBuffer, paddingFrames) => {
  if (!paddingFrames) {
    return audioBuffer;
  }

  const channelCount = Math.max(audioBuffer.numberOfChannels, 1);
  const paddedBuffer = new AudioBuffer({
    length: audioBuffer.length + paddingFrames,
    numberOfChannels: channelCount,
    sampleRate: audioBuffer.sampleRate,
  });

  for (let channelIndex = 0; channelIndex < channelCount; channelIndex += 1) {
    const sourceChannelData = audioBuffer.getChannelData(
      Math.min(channelIndex, audioBuffer.numberOfChannels - 1),
    );
    paddedBuffer.getChannelData(channelIndex).set(sourceChannelData);
  }

  return paddedBuffer;
};

const trimTrailingSilence = (
  samples,
  {
    threshold = TRIM_SILENCE_THRESHOLD,
    channels = 2,
    tailFrames = TRIM_SILENCE_TAIL_FRAMES,
  } = {},
) => {
  let lastNonSilentFrame = -1;
  const frameCount = Math.floor(samples.length / channels);

  for (let frameIndex = frameCount - 1; frameIndex >= 0; frameIndex -= 1) {
    const sampleOffset = frameIndex * channels;
    let hasAudibleSample = false;

    for (let channelIndex = 0; channelIndex < channels; channelIndex += 1) {
      if (Math.abs(samples[sampleOffset + channelIndex] || 0) > threshold) {
        hasAudibleSample = true;
        break;
      }
    }

    if (hasAudibleSample) {
      lastNonSilentFrame = frameIndex;
      break;
    }
  }

  if (lastNonSilentFrame === -1) {
    return samples;
  }

  const trimmedFrameCount = Math.min(
    frameCount,
    lastNonSilentFrame + 1 + tailFrames,
  );

  return samples.slice(0, trimmedFrameCount * channels);
};

const writeString = (view, offset, string) => {
  for (let i = 0; i < string.length; i += 1) {
    view.setUint8(offset + i, string.charCodeAt(i));
  }
};

export const audioBufferToInterleavedSamples = (audioBuffer) => {
  const left = audioBuffer.getChannelData(0);
  const right =
    audioBuffer.numberOfChannels > 1
      ? audioBuffer.getChannelData(1)
      : audioBuffer.getChannelData(0);
  const frameCount = audioBuffer.length;
  const samples = new Float32Array(frameCount * 2);

  for (let i = 0; i < frameCount; i += 1) {
    const sampleIndex = i * 2;
    samples[sampleIndex] = left[i] || 0;
    samples[sampleIndex + 1] = right[i] || 0;
  }

  return samples;
};

export const interleavedSamplesToWavBlob = (samples, sampleRate) => {
  const numberOfChannels = 2;
  const bytesPerSample = 2;
  const blockAlign = numberOfChannels * bytesPerSample;
  const buffer = new ArrayBuffer(44 + samples.length * bytesPerSample);
  const view = new DataView(buffer);

  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + samples.length * bytesPerSample, true);
  writeString(view, 8, 'WAVE');
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numberOfChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bytesPerSample * 8, true);
  writeString(view, 36, 'data');
  view.setUint32(40, samples.length * bytesPerSample, true);

  let offset = 44;
  for (let i = 0; i < samples.length; i += 1) {
    const sample = clamp(samples[i], -1, 1);
    const pcmSample = Math.round(
      sample < 0 ? sample * 0x8000 : sample * 0x7fff,
    );
    view.setInt16(offset, pcmSample, true);
    offset += 2;
  }

  return new Blob([buffer], { type: 'audio/wav' });
};

export const renderProcessedAudioBlob = async ({
  audioUrl,
  audioBlob,
  tempo,
  pitchSemitones,
}) => {
  const decodedAudioBuffer = await decodeAudioBufferFromSource({
    audioUrl,
    audioBlob,
  });
  const safeTempo = clamp(Number(tempo) || 1, 0.5, 2);
  const safePitch = clamp(Number(pitchSemitones) || 0, -12, 12);
  const paddingFrames = Math.ceil(
    decodedAudioBuffer.sampleRate * PROCESSING_TAIL_PADDING_SECONDS,
  );
  const audioBuffer = appendSilenceToAudioBuffer(
    decodedAudioBuffer,
    paddingFrames,
  );
  const source = new WebAudioBufferSource(audioBuffer);
  const soundTouch = new SoundTouch();
  soundTouch.tempo = safeTempo;
  soundTouch.pitchSemitones = safePitch;
  soundTouch.rate = 1;
  const filter = new SimpleFilter(source, soundTouch);
  const chunkFrames = 4096;
  const chunks = [];
  const chunkBuffer = new Float32Array(chunkFrames * 2);
  let totalFrames = 0;

  for (let guard = 0; guard < 100000; guard += 1) {
    const framesRead = filter.extract(chunkBuffer, chunkFrames);
    if (!framesRead) {
      break;
    }

    chunks.push(chunkBuffer.slice(0, framesRead * 2));
    totalFrames += framesRead;

    if (framesRead < chunkFrames) {
      break;
    }
  }

  if (!totalFrames) {
    return interleavedSamplesToWavBlob(
      audioBufferToInterleavedSamples(audioBuffer),
      audioBuffer.sampleRate,
    );
  }

  const mergedSamples = new Float32Array(totalFrames * 2);
  let sampleOffset = 0;
  chunks.forEach((chunk) => {
    mergedSamples.set(chunk, sampleOffset);
    sampleOffset += chunk.length;
  });

  const trimmedSamples = trimTrailingSilence(mergedSamples);

  return interleavedSamplesToWavBlob(trimmedSamples, audioBuffer.sampleRate);
};
